import { Box, CircularProgress } from '@mui/material';
import {
  Button,
  Card,
  CardContent,
  Typography,
} from '@peritasai/ui-components';
import { useTranslation } from 'react-i18next';
import { VERIFY_ASSETS_SUCCESS_COLOR } from '../colors';
import { DetectedCheckIcon } from './DetectedCheckIcon';

export interface ProgressCategoryCount {
  labelKey: string;
  detected: number;
  total: number;
}

export interface ProgressCardProps {
  detectedCount: number;
  totalCount: number;
  categoryCounts: ProgressCategoryCount[];
  isVerified: boolean;
  verifiedByName?: string | null;
  verifiedAt?: Date | null;
  onConfirm: () => void;
  isConfirming: boolean;
}

export function ProgressCard({
  detectedCount,
  totalCount,
  categoryCounts,
  isVerified,
  verifiedByName,
  verifiedAt,
  onConfirm,
  isConfirming,
}: ProgressCardProps) {
  const { t } = useTranslation(['periverse']);
  const percent =
    totalCount > 0 ? Math.round((detectedCount / totalCount) * 100) : 0;
  const isComplete = totalCount > 0 && detectedCount === totalCount;
  const canConfirm = isComplete && !isVerified;

  const title = isVerified
    ? t('pages.caseVerifyAssets.progressCard.titleConfirmed')
    : isComplete
    ? t('pages.caseVerifyAssets.progressCard.titleReady')
    : t('pages.caseVerifyAssets.progressCard.titleVerifying');

  return (
    <Card
      elevation={0}
      sx={{ borderRadius: '8px', width: '251px' }}
      data-testid="progress-card"
    >
      <Box
        sx={{
          backgroundColor: isVerified ? VERIFY_ASSETS_SUCCESS_COLOR : '#1d2a40',
          padding: '8px 16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: 'white', letterSpacing: '1px', lineHeight: 1.6 }}
        >
          {t('pages.caseVerifyAssets.progressCard.overline')}
        </Typography>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '16px',
          '&:last-child': { pb: '16px' },
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={169}
            thickness={5}
            sx={{ color: 'action.disabledBackground', position: 'absolute' }}
            aria-hidden="true"
          />
          <CircularProgress
            variant="determinate"
            value={percent}
            size={169}
            thickness={5}
            sx={{ color: VERIFY_ASSETS_SUCCESS_COLOR }}
            aria-label={t('pages.caseVerifyAssets.progressCard.ariaLabel', {
              count: detectedCount,
              total: totalCount,
            })}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: percent > 0 ? VERIFY_ASSETS_SUCCESS_COLOR : '#121b2a',
                fontSize: '32px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {percent}
              <Box component="span" sx={{ fontSize: '20.64px' }}>
                %
              </Box>
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontSize: '13px',
                fontWeight: 700,
              }}
            >
              {t('pages.caseVerifyAssets.progressCard.fraction', {
                count: detectedCount,
                total: totalCount,
              })}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
          }}
          role="status"
          aria-live="polite"
        >
          {categoryCounts.map((category) => {
            const categoryComplete =
              category.total > 0 && category.detected === category.total;
            return (
              <Box
                key={category.labelKey}
                sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <DetectedCheckIcon
                  sx={{
                    fontSize: 24,
                    color: categoryComplete
                      ? VERIFY_ASSETS_SUCCESS_COLOR
                      : 'action.disabled',
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t(category.labelKey)}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: categoryComplete
                      ? VERIFY_ASSETS_SUCCESS_COLOR
                      : 'text.primary',
                    fontWeight: 700,
                  }}
                >
                  {t('pages.caseVerifyAssets.progressCard.categoryCount', {
                    count: category.detected,
                    total: category.total,
                  })}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {isVerified ? (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              lineHeight: 1.66,
              letterSpacing: '0.4px',
              whiteSpace: 'pre-line',
            }}
            data-testid="verified-sign-off"
          >
            {t('pages.caseVerifyAssets.confirmed.signOff', {
              name: verifiedByName ?? '',
              time: verifiedAt
                ? verifiedAt.toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })
                : '',
            })}
          </Typography>
        ) : (
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={!canConfirm || isConfirming}
            onClick={onConfirm}
            sx={{ borderRadius: '8px' }}
          >
            {isConfirming
              ? t('pages.caseVerifyAssets.progressCard.confirming')
              : t('pages.caseVerifyAssets.progressCard.confirmButton')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
